import { forwardRef, memo, useRef } from 'react';
import PropTypes from 'prop-types';

import { useTheme } from '@mui/material/styles';
import { Badge, Button, Typography } from '@mui/material';
import { Squircle } from 'corner-smoothing';
import MIcon from '../MIcon';

import { ButtonAnimate } from '../../animate';
import getColorFromVariant from '../../../utils/getColorFromVariant';

/**
 * A customizable action button component with support for icons, loading states, badges, and animations.
 * Designed to provide a compact and visually appealing interface for action buttons with optional text and tooltips.
 *
 * @component
 *
 * @param {Object} props - The props object.
 * @param {number} [props.badgeContent] - The content to display in the badge. Displays a dot badge if provided.
 * @param {string} props.icon - The name of the icon to display in the button. Required.
 * @param {string} props.id - A unique identifier for the button. Required.
 * @param {string} [props.iconVariant='primary'] - The variant for the icon color (e.g., 'primary', 'secondary', etc.).
 * @param {number} [props.itemWidth=96] - The width of the button in pixels.
 * @param {boolean} [props.loading=false] - Whether the button is in a loading state.
 * @param {boolean} [props.disabled=false] - Whether the button action is disabled (disables the button if true).
 * @param {string} props.text - The text to display below the icon. Required.
 * @param {string} [props.textVariant='primary'] - The variant for the text color (e.g., 'primary', 'secondary', etc.).
 * @param {string} [props.tooltip] - An optional tooltip text to display when hovering over the button.
 * @param {Function} props.handleClick - The function to call when the button is clicked. Required.
 * @param {Object} [props.other] - Any additional props to be passed to the button component.
 *
 * @returns {JSX.Element} The action button component.
 *
 */
const MActionButton = forwardRef((props, ref) => {
  const {
    badgeContent,
    icon,
    id,
    iconVariant = 'primary',
    itemWidth = 96,
    loading = false,
    disabled = false,
    text,
    textVariant = 'primary',
    tooltip,
    handleClick,
    ...other
  } = props;
  const theme = useTheme();
  const refLabel = useRef();
  const color = getColorFromVariant(theme, textVariant);

  if (!icon || !text) {
    return;
  }

  return (
    <ButtonAnimate {...other} id={id} disabled={disabled} sx={styleButton(theme, itemWidth)} onClick={handleClick}>
      <Squircle className="icon-button-squircle" cornerRadius={12}>
        <Button ref={ref} title={tooltip} loading={loading} disabled={disabled} sx={styleLoadingButton}>
          <Badge
            color="error"
            badgeContent={badgeContent}
            overlap="circular"
            variant={badgeContent ? 'standard' : null}
            sx={styleBadgeAndIcon}
          >
            <MIcon icon={icon} variant={iconVariant} />
          </Badge>

          <Typography ref={refLabel} variant="caption" sx={styleLabel(color)}>
            {text}
          </Typography>
        </Button>
      </Squircle>
    </ButtonAnimate>
  );
});

const styleButton = (theme, itemWidth) => ({
  overflow: 'visible',
  alignSelf: 'center',
  height: '100%',
  '.icon-button-squircle': {
    flexGrow: 1,
    display: 'flex',
    width: itemWidth,
    overflow: 'visible',
    backgroundColor: theme.palette.grey[50],
    border: '1px solid transparent' // Avoid the button width to break
  }
});

const styleLoadingButton = {
  display: 'flex',
  flexFlow: 'column nowrap',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%', // button must not overflow
  py: 1.5,
  px: 1,
  '.MuiLoadingButton-label': {
    maxWidth: '100%', // button must not overflow
    overflow: 'visible',
    flexGrow: 1,
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'center',
    justifyContent: 'flex-start'
  }
};

const styleBadgeAndIcon = {
  maxWidth: '100%' // badge must not overflow
};

const styleLabel = (color) => ({
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'inherit',
  fontSize: { xs: '.75rem', sr: '.85rem' },
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // hyphens: 'auto',
  width: '100%',
  color
});

MActionButton.propTypes = {
  badgeContent: PropTypes.number,
  icon: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  iconVariant: PropTypes.string,
  itemWidth: PropTypes.number,
  loading: PropTypes.bool,
  text: PropTypes.string.isRequired,
  textVariant: PropTypes.string,
  tooltip: PropTypes.string,
  handleClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  caption: PropTypes.string
};

export default memo(MActionButton);
